import { Delete, Edit } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { EntitlementGroupInterface, RelationType } from '@thro-types/access-handler';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import { useLeihbarAccessMutation } from 'api/hooks/leihbar/access';
import { ConfirmationDialog, CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import LeihBar from 'assets/leihBar_Logo.svg';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { LeihbarGroupsDrawer } from './LeihbarGroupsDrawer';

export const Access = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EntitlementGroupInterface | undefined>(undefined);
  const [entitlementGroupIdToDelete, setEntitlementGroupIdToDelete] = useState<number | undefined>(
    undefined
  );

  usePageHeading({ heading: t(translations.pages.leihbar.access.title()), headingLogo: LeihBar });

  const { data: entitlementGroups, isLoading } = useEntitlementGroupsQuery(true, {
    params: { relationType: RelationType.LEIHBAR }
  });

  const { deletion } = useLeihbarAccessMutation();

  const handleDelete = async () => {
    try {
      if (!entitlementGroupIdToDelete) return;
      await deletion.mutateAsync(entitlementGroupIdToDelete);
      dispatch(AlertActions.success(t(translations.pages.leihbar.access.alerts.deleteSuccess())));
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.access.alerts.deleteFailed())));
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams<EntitlementGroupInterface>) => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setSelectedRow(params.row);
            setDrawerOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Delete
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => setEntitlementGroupIdToDelete(params.row.id)}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<EntitlementGroupInterface> => {
    return [
      {
        field: 'name',
        flex: 3,
        headerName: t(translations.pages.leihbar.access.columns.lenderGroup())
      },
      {
        field: 'entitlements',
        flex: 4,
        headerName: t(translations.pages.leihbar.access.columns.entitlements()),
        renderCell: params =>
          params.row.entitlements?.map((entitlement, index) => (
            <Chip key={index} label={entitlement.name} />
          ))
      },
      {
        field: 'actions',
        flex: 0.5,
        type: 'actions',
        headerName: t(translations.common.actions()),
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <Grid container justifyContent="flex-end" gap={1}>
      <ConfirmationDialog
        open={typeof entitlementGroupIdToDelete !== 'undefined'}
        title={t(translations.pages.managePermissionsSettings.deletePermission())}
        onClose={() => {
          setEntitlementGroupIdToDelete(undefined);
        }}
        onConfirm={() => {
          setEntitlementGroupIdToDelete(undefined);
          handleDelete();
        }}
      >
        {t(translations.pages.permissions.deleteConfirm.message())}
      </ConfirmationDialog>
      <LeihbarGroupsDrawer
        open={drawerOpen}
        close={() => {
          setDrawerOpen(false);
          setSelectedRow(undefined);
        }}
        group={selectedRow}
      />
      <CreateButton onClick={() => setDrawerOpen(true)} />
      <DataGrid
        sx={{ height: '75vh' }}
        rows={entitlementGroups?.data || []}
        columns={columns}
        loading={isLoading}
      />
    </Grid>
  );
};
